// src/pages/TicketDesignTemplate/TicketDesignTemplateList.tsx
import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { DESIGN_UNIT_LABELS, DESIGN_UNIT_OPTIONS, STATUS_LABELS, STATUS_OPTIONS, TICKET_DESIGN_TEMPLATE_STATUS, TicketDesignTemplate } from './types';
import moment from 'moment';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_design_template');

interface TicketDesignTemplateListProps {
  templates: TicketDesignTemplate[];
  pagination: any;
  filters: any;
  loading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onFilterChange: (filterName: string, value: any) => void;
  onClearFilters: () => void;
  onCreate: () => void;
  onEdit: (template: TicketDesignTemplate) => void;
  onPreview: (template: TicketDesignTemplate) => void;
  onApprove: (template: TicketDesignTemplate) => void;
  onDelete: (template: TicketDesignTemplate) => void;
  onUnlock?: (template: TicketDesignTemplate) => void;
}

const TicketDesignTemplateList: React.FC<TicketDesignTemplateListProps> = ({
  templates,
  pagination,
  filters,
  loading,
  onPageChange,
  onPageSizeChange,
  onFilterChange,
  onClearFilters,
  onCreate,
  onEdit,
  onPreview,
  onApprove,
  onDelete,
  onUnlock = () => undefined,
}) => {
  const location = useLocation();
  // Tập action-code tĩnh của cột "Thao tác" — dùng để ẩn hẳn cột này khi role hiện tại không có
  // quyền với bất kỳ action nào trong cột (không phụ thuộc trạng thái dòng).
  const TEMPLATE_ACTION_COLUMN_CODES = [
    'button-preview',
    'button-edit',
    'button-approve',
    'button-lock',
    'button-unlock',
  ];
  const canShowActionColumn = PageActionPermission.canDisplayAny(
    location.pathname,
    TEMPLATE_ACTION_COLUMN_CODES,
  );

  // Local state cho ô tìm kiếm — chỉ gọi onFilterChange (kích hoạt gọi API) sau 500ms ngừng gõ
  const [searchValue, setSearchValue] = useState(filters.searchTerm || '');

  useEffect(() => {
    if (searchValue === filters.searchTerm) return;
    const timer = setTimeout(() => {
      onFilterChange('searchTerm', searchValue);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  useEffect(() => {
    if (filters.searchTerm === '') {
      setSearchValue('');
    }
  }, [filters.searchTerm]);

  const formatDate = (date?: string | null) => {
    if (!date) return '-';
    return moment(date).format('DD/MM/YYYY HH:mm');
  };

  const renderStatusBadge = (status?: number, statusDisplayName?: string) => {
    const color =
      status === TICKET_DESIGN_TEMPLATE_STATUS.Approved
        ? 'success'
        : status === TICKET_DESIGN_TEMPLATE_STATUS.Locked
          ? 'secondary'
          : 'warning';
    return (
      <span className={`badge bg-${color}-subtle text-${color} text-uppercase`}>
        {statusDisplayName || STATUS_LABELS[String(status ?? '')] || status}
      </span>
    );
  };

  const renderActions = (template: TicketDesignTemplate) => {
    const isPending = template.status === TICKET_DESIGN_TEMPLATE_STATUS.Pending;
    const isLocked = template.status === TICKET_DESIGN_TEMPLATE_STATUS.Locked || template.isDeleted;
    const hasFile = !!template.fileName || !!template.fileKey;

    return (
      <div className="d-flex justify-content-center gap-1 flex-nowrap">
        {hasFile && PageActionPermission.canDisplay(location.pathname, "button-preview") && (
          <Button
            color="info"
            outline
            size="sm"
            className="btn-icon"
            title={st('xem_truoc_title')}
            data-action-code="button-preview"
            data-action-name="Xem trước mẫu vé"
            onClick={() => onPreview(template)}
          >
            <i className="ri-eye-line"></i>
          </Button>
        )}

        {isPending && !isLocked && (
          <>
            {PageActionPermission.canDisplay(location.pathname, "button-edit") && (
              <Button
                color="primary"
                outline
                size="sm"
                className="btn-icon"
                title={st('sua_title')}
                data-action-code="button-edit"
                data-action-name="Sửa mẫu vé"
                onClick={() => onEdit(template)}
              >
                <i className="ri-pencil-line"></i>
              </Button>
            )}
            {PageActionPermission.canDisplay(location.pathname, "button-approve") && (
              <Button
                color="success"
                outline
                size="sm"
                className="btn-icon"
                title={st('duyet_title')}
                data-action-code="button-approve"
                data-action-name="Duyệt mẫu vé"
                onClick={() => onApprove(template)}
              >
                <i className="ri-check-line"></i>
              </Button>
            )}
          </>
        )}

        {!isLocked && PageActionPermission.canDisplay(location.pathname, "button-lock") && (
          <Button
            color="danger"
            outline
            size="sm"
            className="btn-icon"
            title={st('khoa_text')}
            data-action-code="button-lock"
            data-action-name="Khóa mẫu vé"
            onClick={() => onDelete(template)}
          >
            <i className="ri-lock-line"></i>
          </Button>
        )}

        {isLocked && PageActionPermission.canDisplay(location.pathname, "button-unlock") && (
          <Button
            color="success"
            outline
            size="sm"
            className="btn-icon"
            title={st('mo_khoa_title')}
            data-action-code="button-unlock"
            data-action-name="Mở khóa mẫu vé"
            onClick={() => onUnlock(template)}
          >
            <i className="ri-lock-unlock-line"></i>
          </Button>
        )}
      </div>
    );
  };

  return (
    <Card id="ticketDesignTemplateList">
      <CardHeader className="card-header border-0">
        <Row className="align-items-center gy-3">
          <div className="col-sm">
            <h5 className="card-title mb-0">{st('danh_sach_mau_thiet_ke_ve_title')}</h5>
          </div>
          <div className="col-sm-auto">
            <div className="d-flex gap-1 flex-wrap">
              {PageActionPermission.canDisplay(location.pathname, "button-add") && (
                <Button
                  color="success"
                  className="add-btn"
                  data-action-code="button-add"
                  data-action-name="Thêm mẫu vé mới"
                  onClick={onCreate}
                >
                  <i className="ri-add-line align-bottom me-1"></i>
                  {st('them_moi_button')}
                </Button>
              )}
              <Button color="soft-secondary" onClick={onClearFilters}>
                <i className="ri-refresh-line align-bottom me-1"></i>
                {st('xoa_loc_button')}
              </Button>
            </div>
          </div>
        </Row>
      </CardHeader>

      <CardBody className="pt-0">
        <Row className="g-3 mb-3 align-items-end mb-5">
          <Col md={3}>
            <Label className="form-label">{st('tim_kiem_label')}</Label>
            <Input
              type="text"
              placeholder={st('tim_theo_ten_ma_mau_placeholder')}
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </Col>
          <Col md={3}>
            <Label className="form-label">{st('trang_thai_label')}</Label>
            <Input
              type="select"
              className="form-select"
              value={filters.status}
              onChange={(event) => onFilterChange('status', event.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </Col>
          <Col md={3}>
            <Label className="form-label">{st('design_unit_label')}</Label>
            <Input
              type="select"
              className="form-select"
              value={filters.designUnit}
              onChange={(event) => onFilterChange('designUnit', event.target.value)}
            >
              <option value="All">{st('tat_ca_option')}</option>
              {DESIGN_UNIT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </Col>
          <Col md={3}>
            <Label className="form-label">{st('issuance_period_label')}</Label>
            <Input
              type="text"
              placeholder={st('issuance_period_placeholder')}
              value={filters.issuancePeriod}
              onChange={(event) => onFilterChange('issuancePeriod', event.target.value)}
            />
          </Col>
        </Row>

        <DataTableShell
          headerRow={
            <tr>
              <th className="text-center">{st('stt_column')}</th>
              <th>{st('ma_mau_text')}</th>
              <th>{st('template_name_label')}</th>
              <th>{st('design_unit_label')}</th>
              <th>{st('issuance_period_label')}</th>
              <th>{st('trang_thai_label')}</th>
              <th>{st('nguoi_duyet_column')}</th>
              <th>{st('ngay_duyet_column')}</th>
              {canShowActionColumn && (
                <th style={{ width: 160 }} className="text-center">{st('thao_tac_column')}</th>
              )}
            </tr>
          }
        >
              {loading ? (
                Array.from({ length: 5 }).map((_, row) => (
                  <tr key={row}>
                    {Array.from({ length: canShowActionColumn ? 9 : 8 }).map((__, col) => (
                      <td key={col}>
                        <div className="placeholder-glow">
                          <span className="placeholder col-8"></span>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : templates.length === 0 ? (
                <tr>
                  <td colSpan={canShowActionColumn ? 9 : 8} className="text-center text-muted py-5">
                    <div className="mb-2">
                      <i className="ri-file-list-3-line fs-1 text-muted"></i>
                    </div>
                    {st('khong_co_du_lieu_text')}
                  </td>
                </tr>
              ) : (
                templates.map((template, index) => (
                  <tr key={template.id}>
                    <td className="text-center">
                      {getSttNumber(pagination.currentPage, pagination.pageSize, index)}
                    </td>
                    <td>
                      <strong>{template.templateCode}</strong>
                    </td>
                    <td>{template.templateName}</td>
                    <td>{DESIGN_UNIT_LABELS[template.designUnit || ''] || template.designUnit}</td>
                    <td>{template.issuancePeriod}</td>
                    <td>{renderStatusBadge(template.status, template.statusName || template.statusDisplayName)}</td>
                    <td>{template.approverName || '-'}</td>
                    <td>{formatDate(template.approvedAt)}</td>
                    {canShowActionColumn && (
                      <td className="text-center">{renderActions(template)}</td>
                    )}
                  </tr>
                ))
              )}
        </DataTableShell>

        {!loading && templates.length > 0 && (
          <Row className="mt-3 align-items-center">
            <Col md={6}>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <Label className="mb-0">{st('hien_thi_label')}</Label>
                <Input
                  type="select"
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                  value={pagination.pageSize}
                  onChange={(event) => onPageSizeChange(Number(event.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>
                <span className="text-muted">
                  {st('tong_text')} {pagination.totalItems} {st('ban_ghi_text')}
                </span>
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex justify-content-end">
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => onPageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                      >
                        {st('truoc_button')}
                      </button>
                    </li>
                    {Array.from({ length: pagination.totalPages }).map((_, index) => (
                      <li
                        key={index + 1}
                        className={`page-item ${pagination.currentPage === index + 1 ? 'active' : ''}`}
                      >
                        <button className="page-link" onClick={() => onPageChange(index + 1)}>
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => onPageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages}
                      >
                        {st('sau_button')}
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  );
};

export default TicketDesignTemplateList;
